import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  LinkedinLogo,
  WhatsappLogo,
  EnvelopeSimple,
  ArrowRight,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sectionLinks = [
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "markets", label: "Markets" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

const legalLinks = [
  { to: "/industries", label: "Industries" },
  { to: "/global-network", label: "Global Network" },
  { to: "/leadership", label: "Leadership" },
  { to: "/compliance", label: "Compliance" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
  { to: "/cookie-policy", label: "Cookie Policy" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goSection = (id) => {
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed. Welcome to the Altin Link circle.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Subscription failed.";
      toast.error(typeof detail === "string" ? detail : "Subscription failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-altin-ink text-white pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <img
              src="/altin-logo-sm.png"
              alt="Altin Link Danışmanlık Hizmetleri Ltd. Şti."
              className="h-24 w-auto object-contain"
            />
            <p className="mt-6 text-white/70 leading-relaxed max-w-md font-light">
              Connecting Businesses. Creating Opportunities. Building Long-Term
              Partnerships across Türkiye, Europe, the Middle East and beyond.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/altin-link"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
                className="w-10 h-10 grid place-items-center border border-white/20 hover:border-altin-gold hover:bg-altin-gold transition-colors"
              >
                <LinkedinLogo size={18} weight="light" />
              </a>
              <a
                href="https://wa.me/902128300378"
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp"
                className="w-10 h-10 grid place-items-center border border-white/20 hover:border-altin-gold hover:bg-altin-gold transition-colors"
              >
                <WhatsappLogo size={18} weight="light" />
              </a>
              <a
                href="mailto:info@altin-link.com"
                aria-label="Email"
                data-testid="footer-email-icon"
                className="w-10 h-10 grid place-items-center border border-white/20 hover:border-altin-gold hover:bg-altin-gold transition-colors"
              >
                <EnvelopeSimple size={18} weight="light" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-5">Quick Links</div>
            <ul className="space-y-3">
              {sectionLinks.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => goSection(l.id)}
                    data-testid={`footer-link-${l.id}`}
                    className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-altin-gold transition-colors"
                  >
                    {l.label}
                    <ArrowUpRight size={12} weight="light" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-5">Legal & Company</div>
            <ul className="space-y-3">
              {legalLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-altin-gold transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline mb-5">Newsletter</div>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              Quarterly insights on commercial flows between Türkiye, Europe and
              the Middle East — straight to your inbox.
            </p>
            <form
              onSubmit={subscribe}
              className="flex items-center border border-white/20 focus-within:border-altin-gold transition-colors w-full min-w-0"
              data-testid="newsletter-form"
            >
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-input"
                className="flex-1 min-w-0 bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="shrink-0 px-5 py-3 bg-altin-gold text-altin-ink hover:bg-altin-goldHover transition-colors disabled:opacity-60"
                aria-label="Subscribe"
              >
                <ArrowRight size={18} weight="light" />
              </button>
            </form>

            <div className="mt-8">
              <div className="overline mb-3">Get in touch</div>
              <ul className="space-y-2 text-sm text-white/80">
                <li>Istanbul · Venezia Mall, J Block No: 01</li>
                <li>
                  <a href="mailto:info@altin-link.com" className="hover:text-altin-gold transition-colors">
                    info@altin-link.com
                  </a>
                </li>
                <li>
                  <a href="tel:+902128300378" className="hover:text-altin-gold transition-colors">
                    Tel: +90 212 830 03 78
                  </a>
                </li>
                <li>
                  <a href="tel:+902128031275" className="hover:text-altin-gold transition-colors">
                    Tel: +90 212 803 12 75
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div
          data-testid="footer-company-info"
          className="mt-16 pt-10 border-t border-white/10 grid grid-cols-2 md:grid-cols-5 gap-6"
        >
          {[
            { k: "Company Name", v: "ALTIN LINK DANIŞMANLIK HİZMETLERİ LTD. ŞTİ." },
            { k: "Tax Number", v: "0610962811" },
            { k: "Country", v: "Türkiye" },
            { k: "City", v: "İstanbul" },
            {
              k: "Official Website",
              v: "www.altin-link.com",
              href: "https://www.altin-link.com",
            },
          ].map((item) => (
            <div key={item.k}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-altin-gold mb-2">
                {item.k}
              </div>
              {item.href ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-white/80 hover:text-altin-gold transition-colors break-words"
                >
                  {item.v}
                </a>
              ) : (
                <div className="text-sm text-white/80 break-words leading-snug">
                  {item.v}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <p
          data-testid="footer-disclaimer"
          className="mt-10 text-xs text-white/60 leading-relaxed max-w-4xl"
        >
          Disclaimer: Altin Link Danışmanlık Hizmetleri Ltd. Şti. acts solely as
          an independent commercial broker and business consultant. We are not a
          party to any commercial contract concluded between introduced parties
          and do not provide legal, financial, tax or investment advice. All
          information on this website is provided for general informational
          purposes only and does not constitute an offer, solicitation or
          warranty of any kind. Business partnerships are subject to independent
          due diligence and separate written agreements.
        </p>

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-sm text-white/50">
            © {year} ALTIN LINK DANIŞMANLIK HİZMETLERİ LTD. ŞTİ. All Rights
            Reserved.
          </p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/60">
            <li>
              <Link to="/privacy-policy" className="hover:text-altin-gold transition-colors uppercase tracking-[0.2em]">
                Privacy
              </Link>
            </li>
            <li>
              <Link to="/terms" className="hover:text-altin-gold transition-colors uppercase tracking-[0.2em]">
                Terms
              </Link>
            </li>
            <li>
              <Link to="/cookie-policy" className="hover:text-altin-gold transition-colors uppercase tracking-[0.2em]">
                Cookies
              </Link>
            </li>
            <li>
              <Link to="/compliance" className="hover:text-altin-gold transition-colors uppercase tracking-[0.2em]">
                Compliance
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
